import { useState } from "react";
import { Page, useCurrent } from "./common";
import { PageHead } from "../components/ui";
import { Icon } from "../components/Icons";
import { fmtDate, relative } from "../lib/format";
import { printTable } from "../lib/printView";
import { RecordsPanel } from "../components/RecordStatus";

/* The audit trail — every recorded action on the project, filterable and
   exportable. The log was always being written; this is where you read it. */
const KIND_HUE: Record<string, string> = { design: "var(--accent)", payment: "var(--ok)", stage: "var(--sky)", feedback: "var(--warn)" };

export default function Audit() {
  const p = useCurrent();
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<string>("all");
  const [who, setWho] = useState<string>("all");
  if (!p) return null;

  const all = [...(p.activity ?? [])].sort((a, b) => +new Date(b.on) - +new Date(a.on));
  const kinds = [...new Set(all.map((a) => a.kind).filter(Boolean))] as string[];
  const people = [...new Set(all.map((a) => a.who).filter(Boolean))] as string[];
  const list = all.filter((a) =>
    (kind === "all" || a.kind === kind) &&
    (who === "all" || a.who === who) &&
    (!q.trim() || `${a.who} ${a.what}`.toLowerCase().includes(q.trim().toLowerCase())));

  function exportCsv() {
    const rows = [["When", "Who", "Action", "Kind"], ...list.map((a) => [a.on, a.who ?? "", a.what, a.kind ?? ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const el = document.createElement("a"); el.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); el.download = `${(p!.code || p!.name).replace(/[^\w]+/g, "_")}_audit.csv`; el.click();
  }

  return (
    <Page>
      <PageHead kicker={`${p.name} · ${p.code}`} title="Audit trail" />
      <p className="muted reveal" style={{ maxWidth: 640, marginBottom: 18 }}>
        Everything that has happened on this project, in order — who did it and when. Useful when a date or an approval is ever in question.
      </p>
      <div style={{ marginBottom: 22 }}>
        <RecordsPanel p={p} intro="Each formal event is emailed to the client and to the studio the moment it happens. The outcome of every send is kept here; a failed or unsent copy can be sent again." />
      </div>

      <div className="row wrap reveal" style={{ gap: 8, marginBottom: 18, alignItems: "center" }}>
        <div className="row" style={{ gap: 8, flex: "1 1 240px", minWidth: 200, position: "relative" }}>
          <Icon.brief width={14} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "var(--ink-3)", pointerEvents: "none" }} />
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the log…" style={{ paddingLeft: 32, width: "100%" }} />
        </div>
        <select className="select" style={{ width: "auto", fontSize: 12.5 }} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="all">All kinds</option>
          {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <select className="select" style={{ width: "auto", fontSize: 12.5 }} value={who} onChange={(e) => setWho(e.target.value)}>
          <option value="all">Everyone</option>
          {people.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
        <span className="dim" style={{ fontSize: 12 }}>{list.length} of {all.length}</span>
        <span style={{ flex: 1 }} />
        {list.length > 0 && <button className="btn ghost sm" onClick={() => printTable({
          title: "Audit trail", subtitle: `${p!.name} · ${p!.code}`, meta: [`${list.length} entries`, new Date().toLocaleDateString()],
          columns: [{ key: "when", label: "When", width: "90px" }, { key: "who", label: "Who", width: "110px" }, { key: "what", label: "Action" }, { key: "kind", label: "Kind", width: "70px" }],
          rows: list.map((a) => ({ when: fmtDate(a.on), who: a.who ?? "", what: a.what, kind: a.kind ?? "" })),
        })}><Icon.report width={13} />Print</button>}
        {list.length > 0 && <button className="btn ghost sm" onClick={exportCsv}><Icon.download width={13} />CSV</button>}
      </div>

      {list.length === 0 ? (
        <div className="dim reveal" style={{ padding: "24px 4px", fontSize: 14 }}>Nothing matches.</div>
      ) : (
        <div className="card reveal" style={{ overflow: "hidden" }}>
          {list.map((a, i) => (
            <div key={a.id} className="row between wrap" style={{ padding: "11px 16px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
              <span className="row" style={{ gap: 10, minWidth: 0 }}>
                <span style={{ width: 7, height: 7, borderRadius: 7, background: KIND_HUE[a.kind ?? ""] ?? "var(--ink-3)", flexShrink: 0 }} />
                <span style={{ fontSize: 13.5 }}><b style={{ fontWeight: 600 }}>{a.who}</b> {a.what}</span>
              </span>
              <span className="dim" style={{ fontSize: 11.5, flexShrink: 0 }} title={a.on}>{fmtDate(a.on)} · {relative(a.on)}</span>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}
